import React from 'react';

import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  PaginationContext,
  PaginationContextValue,
} from './PaginationContext';

export type PaginationConfig = {
  page?: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
};

export interface PaginationProviderProps {
  paginationConfig?: PaginationConfig;
}

export const PaginationProvider: React.FC<React.PropsWithChildren<PaginationProviderProps>> = ({
  children,
  paginationConfig,
}) => {
  const [page, setPage] = React.useState(paginationConfig?.page || DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    paginationConfig?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
  );

  const rowsPerPageOptions: PaginationContextValue['rowsPerPageOptions'] = React.useMemo(
    () => paginationConfig?.rowsPerPageOptions || DEFAULT_ROWS_PER_PAGE_OPTIONS,
    [paginationConfig?.rowsPerPageOptions],
  );

  const resetPage: PaginationContextValue['resetPage'] = React.useCallback(() => {
    setPage(DEFAULT_PAGE);
  }, []);

  const onModelChange: PaginationContextValue['onModelChange'] = React.useCallback(
    (page, rowsPerPage) => {
      setPage(page);
      setRowsPerPage(rowsPerPage);
    },
    [],
  );

  const contextValue = React.useMemo((): PaginationContextValue => {
    return {
      page,
      rowsPerPage,
      rowsPerPageOptions,
      onModelChange,
      resetPage,
    };
  }, [onModelChange, page, resetPage, rowsPerPage, rowsPerPageOptions]);

  return <PaginationContext value={contextValue}>{children}</PaginationContext>;
};

PaginationProvider.displayName = 'PaginationProvider';
